import {
  averageConsumptionKgPer100Km,
  isStaleReading,
  tripDistanceKm,
  TripDistance,
} from "./fuelCellMetrics";
import { FuelCellRow, FuelCellSample } from "./FuelCellRow";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";

const REFILL_THRESHOLD_PERCENT = 10;
const NOISE_THRESHOLD_PERCENT = 0.5;

type FuelTankCapacityValue =
  | number
  | { volume?: number | null; source?: unknown }
  | null
  | undefined;

export type DeviceWithFuelTank = DeviceWithVehicleInfo & {
  fuelTankCapacity?: FuelTankCapacityValue;
};

/** The newest live tank-level status entry of a device, the fallback where nothing was reported. */
export interface LiveLevelEntry {
  data?: unknown;
  dateTime?: string | Date;
}

function getTankCapacityKg(device: DeviceWithFuelTank): number | null {
  const capacity = device.fuelTankCapacity;
  if (typeof capacity === "number" && Number.isFinite(capacity)) {
    return capacity;
  }
  if (
    capacity &&
    typeof capacity === "object" &&
    typeof capacity.volume === "number" &&
    Number.isFinite(capacity.volume)
  ) {
    return capacity.volume;
  }
  return null;
}

function summarizeHistory(sorted: FuelCellSample[]): {
  usedPercent: number;
  refillCount: number;
} | null {
  if (sorted.length < 2) {
    return null;
  }
  let usedPercent = 0;
  let refillCount = 0;
  for (let i = 1; i < sorted.length; i++) {
    const delta = sorted[i].data - sorted[i - 1].data;
    if (delta >= REFILL_THRESHOLD_PERCENT) {
      refillCount++;
    } else if (delta < -NOISE_THRESHOLD_PERCENT) {
      usedPercent += -delta;
    }
  }
  return { usedPercent, refillCount };
}

/**
 * The grid row of one fuel-cell vehicle over a period.
 *
 * @param history - The vehicle's tank-level samples, sorted by time; `undefined` while not read.
 * @param trips - The vehicle's trips; `undefined` while not read.
 * @param liveEntry - Live tank level, used where no sample arrived inside the period.
 * @param minDistanceKm - See {@link averageConsumptionKgPer100Km}.
 */
export function buildFuelCellRow(
  device: DeviceWithFuelTank,
  history: FuelCellSample[] | undefined,
  trips: TripDistance[] | undefined,
  liveEntry: LiveLevelEntry | undefined,
  minDistanceKm: number,
): FuelCellRow {
  const tankCapacityKg = getTankCapacityKg(device);

  const identity = {
    id: device.id,
    name: device.name ?? "",
    licensePlate: device.licensePlate ?? "",
    vin: device.vehicleIdentificationNumber ?? "",
    serialNumber: device.serialNumber ?? "",
    tankCapacityKg,
  };

  // The last level reported inside the period, not the level right now. The whole row describes
  // the period, and a "now" figure next to per-period figures reads as one of them. The live
  // status is the fallback for a vehicle that reported nothing in between.
  const latestSample = history?.at(-1);
  const rawLevel = latestSample?.data ?? liveEntry?.data;
  const levelPercent = typeof rawLevel === "number" && Number.isFinite(rawLevel) ? rawLevel : null;
  const reportedAt = latestSample?.dateTime ?? liveEntry?.dateTime;

  const remainingKg =
    levelPercent != null && tankCapacityKg != null ? (levelPercent / 100) * tankCapacityKg : null;
  const usedKg =
    levelPercent != null && tankCapacityKg != null
      ? ((100 - levelPercent) / 100) * tankCapacityKg
      : null;

  const summary = history ? summarizeHistory(history) : null;
  const usedRangeKg =
    summary && tankCapacityKg != null ? (summary.usedPercent / 100) * tankCapacityKg : null;

  const distanceKm = tripDistanceKm(trips);

  return {
    ...identity,
    levelPercent,
    remainingKg,
    usedKg,
    updatedAt: reportedAt ? new Date(reportedAt) : null,
    isStale: isStaleReading(distanceKm, latestSample != null),
    usedRangeKg,
    refillCount: summary ? summary.refillCount : null,
    distanceKm,
    avgConsumptionKgPer100Km: averageConsumptionKgPer100Km(usedRangeKg, distanceKm, minDistanceKm),
  };
}
